import { AddFilledIcon } from '@calizahq/react-components-v2'
import { css } from '@emotion/react'
import clsx from 'clsx'
import React, { Children, forwardRef, PropsWithChildren, useRef } from 'react'
import { Transition, TransitionStatus } from 'react-transition-group'
import { useClickAway } from 'react-use'

type Props = {
  isShown: boolean
  onClose?(): void
  toggle(): void
}

export const FAB = ({ children, onClose, isShown, toggle }: PropsWithChildren<Props>) => {
  const fobRef = useRef<HTMLDivElement | null>(null)
  const slideRef = useRef<HTMLDivElement | null>(null)
  const isSingleOption = Children.count(children) === 1

  useClickAway(fobRef, () => {
    if (isShown) toggle()
  })

  function handleExit() {
    onClose && onClose()
  }

  function handleClick() {
    if (isSingleOption) {
      const child = Children.only(children) as React.ReactElement<{ onClick?: () => void }>
      child.props.onClick?.()
    } else {
      toggle()
    }
  }

  return (
    <div
      className="fixed bottom-4  right-4 z-30 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-action-primary"
      onClick={handleClick}
      ref={fobRef}
      role={'presentation'}
      style={{ boxShadow: '3px 4px 4px rgba(0, 0, 0, 0.50)' }}
    >
      <AddFilledIcon className="h-6 w-6 text-semantic-white"></AddFilledIcon>

      {!isSingleOption && (
        <Transition in={isShown} nodeRef={slideRef} onExited={handleExit} timeout={100}>
          {(state) => (
            <SlideOptions
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              ref={slideRef}
              role={'presentation'}
              state={state}
              style={{ boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.12)' }}
            >
              {children}
            </SlideOptions>
          )}
        </Transition>
      )}
    </div>
  )
}

const slideOptionsStyles = (state: TransitionStatus) => css`
  transition: 0.25s;
  display: ${state === 'exited' ? 'none' : 'flex  flex-col'};

  .option {
    font-weight: 500;
  }
`

const SlideOptions = forwardRef<HTMLDivElement, { state: TransitionStatus } & React.ComponentPropsWithoutRef<'div'>>(
  function SlideOptions({ state, className, ...props }, ref) {
    return (
      <div
        {...props}
        className={clsx(
          'absolute bottom-8 right-0 rounded-lg border border-border-secondary bg-primary px-4 py-2 opacity-0',
          state === 'entered' && 'opacity-100 bottom-14',
          (state === 'exited' || state === 'exiting') && 'pointer-events-none',
          className,
        )}
        css={slideOptionsStyles(state)}
        ref={ref}
      />
    )
  },
)
